import { useEffect, useState } from 'react';
import { patientPortal } from '@/api/patientPortal';
import PatientPortalPageHero from './PatientPortalPageHero';
import { LOYALTY_REDEEM_HOLD_MESSAGE } from '@/api/loyalty';

type BurnRule = {
  purpose: string;
  isActive: boolean;
  pointsCost: number | null;
  minPointsToRedeem: number;
};

export default function PatientPortalWallet() {
  const [wallet, setWallet] = useState<{
    pointsBalance: number;
    redeemableBalance?: number;
    pointsOnHold?: number;
    redeemHoldMessage?: string;
    nextRedeemableAt?: string | null;
    cashbackBalance: number;
    promoBalance: number;
    refundBalance: number;
    rupeeValue: number;
    burnRules: BurnRule[];
  } | null>(null);
  const [offers, setOffers] = useState<
    Array<{ id: string; title: string; body: string; pointsReward: number; promoCreditAmount: number }>
  >([]);
  const [purpose, setPurpose] = useState('');
  const [points, setPoints] = useState('');
  const [msg, setMsg] = useState('');

  const load = () => {
    patientPortal.getWallet().then(setWallet).catch(() => {});
    patientPortal.getOffers().then(setOffers).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const redeem = async () => {
    setMsg('');
    const pts = Number(points);
    if (!purpose || !pts) {
      setMsg('Select purpose and enter points');
      return;
    }
    try {
      const r = await patientPortal.redeemPoints(pts, purpose);
      setMsg(`Redeemed ${r.pointsBurned} points (₹${r.rupeeValue})`);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setMsg(err.response?.data?.message ?? 'Redemption failed');
    }
  };

  const redeemable = wallet?.redeemableBalance ?? wallet?.pointsBalance ?? 0;
  const onHold = wallet?.pointsOnHold ?? 0;
  const holdMessage = wallet?.redeemHoldMessage ?? LOYALTY_REDEEM_HOLD_MESSAGE;
  const nextAt = wallet?.nextRedeemableAt
    ? new Date(wallet.nextRedeemableAt).toLocaleString()
    : null;

  return (
    <div className="space-y-8 max-w-lg">
      <PatientPortalPageHero
        imageKey="wallet"
        eyebrow="Rewards"
        title="Digital wallet"
        subtitle="Redeem points, view cashback, and browse active offers"
        compact
      />

      <div className="grid grid-cols-2 gap-3">
        <BalanceCard label="Redeemable points" value={redeemable} />
        <BalanceCard label="≈ Rupees" value={`₹${wallet?.rupeeValue?.toFixed(2) ?? '0'}`} />
        <BalanceCard label="Cashback" value={`₹${wallet?.cashbackBalance?.toFixed(2) ?? '0'}`} />
        <BalanceCard label="Promo" value={`₹${wallet?.promoBalance?.toFixed(2) ?? '0'}`} />
      </div>

      <p className="text-sm text-amber-900 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
        {holdMessage}
        {onHold > 0 ? ` ${onHold} points are on hold (${wallet?.pointsBalance ?? 0} total).` : ''}
        {nextAt ? ` Next unlock: ${nextAt}.` : ''}
      </p>

      <div>
        <h2 className="font-semibold mb-2">Redeem points</h2>
        <div className="border rounded-lg bg-white p-4 space-y-3">
          <select
            className="w-full border rounded px-3 py-2 text-sm"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
          >
            <option value="">Select redemption type</option>
            {(wallet?.burnRules ?? [])
              .filter((r) => r.isActive)
              .map((r) => (
                <option key={r.purpose} value={r.purpose}>
                  {r.purpose.replace(/_/g, ' ')}
                  {r.pointsCost != null ? ` (${r.pointsCost} pts)` : ` (min ${r.minPointsToRedeem})`}
                </option>
              ))}
          </select>
          <input
            type="number"
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder="Points to redeem"
            value={points}
            onChange={(e) => setPoints(e.target.value)}
          />
          <button
            type="button"
            onClick={redeem}
            className="w-full bg-teal-600 text-white rounded-lg py-2 font-medium"
          >
            Redeem
          </button>
          {msg ? <p className="text-sm text-slate-600">{msg}</p> : null}
        </div>
      </div>

      <div>
        <h2 className="font-semibold mb-2">Offers & campaigns</h2>
        <ul className="border rounded-lg bg-white divide-y text-sm">
          {offers.length === 0 ? (
            <li className="p-4 text-muted-foreground">No active offers</li>
          ) : (
            offers.map((o) => (
              <li key={o.id} className="p-3">
                <p className="font-medium">{o.title}</p>
                <p className="text-muted-foreground mt-1">{o.body}</p>
                {(o.pointsReward > 0 || o.promoCreditAmount > 0) && (
                  <p className="text-teal-700 text-xs mt-1">
                    {o.pointsReward > 0 && `+${o.pointsReward} pts`}
                    {o.promoCreditAmount > 0 && ` · ₹${o.promoCreditAmount} promo`}
                  </p>
                )}
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

function BalanceCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="border rounded-lg bg-white p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold text-teal-800">{value}</p>
    </div>
  );
}
